import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { setNotificationsSeen } from '../../../actions';
import { Consts } from '../../../res';
import { markNotificationsRead } from '../services/notifications.service';
import useNotifications from './useNotifications';

/**
 * The unread `IMPORTANT` notifications, and how they stop being unread.
 *
 * What the old `MessagesButton` did, split the way it split it: the server
 * hears they were read as soon as they are on screen, the store only when the
 * reader closes the dialogue.
 *
 * Posting on dismiss instead would lose the read to a link inside the
 * dialogue — those are plain `href`s, and a page load cancels a request still
 * in flight. Updating the store on show would shut the dialogue the moment it
 * opened, because it is open exactly while something here is unread.
 *
 * Only `ImportantNotifications` calls this, once, from the layout. A second
 * caller would post every batch twice.
 */
const useImportantNotifications = () => {
    const dispatch = useDispatch();
    const { items } = useNotifications();
    const posted = useRef(new Set());

    const unread = useMemo(
        () => items.filter((n) => n.isUnread && n.type === Consts.NOTIFICATIONS_TYPES.IMPORTANT),
        [items]
    );

    useEffect(() => {
        const fresh = unread.map((n) => n.notificationId).filter((id) => !posted.current.has(id));
        if (!fresh.length) return;

        fresh.forEach((id) => posted.current.add(id));
        // Swallowed, like the bell's "مسح الكل": the worst a failure does is
        // bring the dialogue back on the next load.
        markNotificationsRead(fresh).catch(() => {});
    }, [unread]);

    const dismiss = useCallback(() => {
        const ids = unread.map((n) => n.notificationId);
        if (ids.length) dispatch(setNotificationsSeen(ids));
    }, [dispatch, unread]);

    return { items: unread, dismiss };
};

export default useImportantNotifications;
